/**
 * User Date Utilities
 * 
 * Provides consistent user date calculation and formatting for API transmission.
 * This utility replaces complex timezone logic with user-centric date validation.
 * 
 * Key principles:
 * - Calculate user's local date as ISO string (YYYY-MM-DD)
 * - Handle edge cases like timezone changes and system time issues
 * - Provide fallback behavior when calculation fails
 * - Ensure consistent format across all API requests
 */

/**
 * Calculates the user's current local date as an ISO string (YYYY-MM-DD)
 * 
 * This function provides the user's local date in a timezone-agnostic format
 * that can be safely transmitted to the backend for validation.
 * 
 * The backend will validate this date against server time with ±2 day tolerance
 * to detect clock manipulation while accommodating legitimate timezone differences.
 * 
 * @returns ISO date string in YYYY-MM-DD format (e.g., "2025-11-21")
 * @throws Error if date calculation fails (caller should handle gracefully)
 */
export function getUserDate(): string {
  try {
    const now = new Date();
    
    // Validate that we have a valid Date object
    if (isNaN(now.getTime())) {
      throw new Error('Invalid system time detected');
    }
    
    // Get the user's local date in YYYY-MM-DD format
    // This uses the browser's local timezone without complex timezone math
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    
    const userDateString = `${year}-${month}-${day}`;
    
    // Validate the format matches our expected pattern
    if (!/^\d{4}-\d{2}-\d{2}$/.test(userDateString)) {
      throw new Error('Generated date does not match expected format');
    }
    
    return userDateString;
  } catch (error) {
    // Re-throw with additional context for debugging
    throw new Error(`Failed to calculate user date: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Calculates the user's local date with graceful fallback handling
 * 
 * This is the recommended function to use in production code as it provides
 * robust error handling and fallback behavior when date calculation fails.
 * 
 * @param fallbackDate - Optional fallback date to use if calculation fails
 * @returns Object containing the calculated date and success status
 */
export function getUserDateSafe(fallbackDate?: string): { 
  userDate: string; 
  success: boolean; 
  error?: string 
} {
  try {
    const userDate = getUserDate();
    return {
      userDate,
      success: true
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    
    // Use provided fallback or calculate a basic fallback
    let safeFallback: string;
    
    if (fallbackDate && isValidUserDate(fallbackDate)) {
      safeFallback = fallbackDate;
    } else {
      // Generate a basic fallback using simple date methods
      try {
        const now = new Date();
        safeFallback = now.toISOString().split('T')[0];
      } catch {
        // Ultimate fallback if everything else fails
        safeFallback = '2025-01-01';
      }
    }
    
    return {
      userDate: safeFallback,
      success: false,
      error: errorMessage
    };
  }
}

/**
 * Validates that a date string matches the expected YYYY-MM-DD format
 * 
 * This function only checks the format, not whether the date is valid
 * (e.g., 2025-13-01 would pass format check but is not a valid date).
 * 
 * @param dateString - The date string to validate
 * @returns true if the format is valid, false otherwise
 */
export function isValidUserDateFormat(dateString: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
}

/**
 * Validates that a date string is both correctly formatted and represents a valid date
 * 
 * @param dateString - The date string to validate
 * @returns true if the format and date value are valid, false otherwise
 */
export function isValidUserDate(dateString: string): boolean {
  if (!isValidUserDateFormat(dateString)) {
    return false;
  }
  
  try {
    parseUserDate(dateString);
    return true;
  } catch {
    return false;
  }
}

/**
 * Parses a user date string into a Date object at midnight UTC
 * 
 * This function ensures consistent date parsing for comparison purposes.
 * The backend uses the same parsing logic for validation.
 * 
 * @param userDateString - ISO date string (YYYY-MM-DD)
 * @returns Date object at midnight UTC for the given date
 * @throws Error if the date string is invalid
 */
export function parseUserDate(userDateString: string): Date {
  if (!isValidUserDateFormat(userDateString)) {
    throw new Error(`Invalid user date format: ${userDateString}. Expected YYYY-MM-DD`);
  }
  
  // Parse components manually for strict validation
  const [year, month, day] = userDateString.split('-').map(Number);
  
  // Validate ranges
  if (year < 1000 || year > 9999) {
    throw new Error(`Invalid date value: ${userDateString}`);
  }
  if (month < 1 || month > 12) {
    throw new Error(`Invalid date value: ${userDateString}`);
  }
  if (day < 1 || day > 31) {
    throw new Error(`Invalid date value: ${userDateString}`);
  }
  
  // Create date and validate it matches input (handles invalid dates like Feb 30)
  const parsedDate = new Date(userDateString + 'T00:00:00.000Z');
  
  if (isNaN(parsedDate.getTime())) {
    throw new Error(`Invalid date value: ${userDateString}`);
  }
  
  // Check if the date was adjusted (e.g., Feb 30 becomes Mar 2)
  if (parsedDate.getUTCFullYear() !== year ||
      parsedDate.getUTCMonth() !== (month - 1) ||
      parsedDate.getUTCDate() !== day) {
    throw new Error(`Invalid date value: ${userDateString}`);
  }
  
  return parsedDate;
}

/**
 * Gets the current time for performance tracking and debugging
 * 
 * @returns Current timestamp in milliseconds
 */
export function getCurrentTimestamp(): number {
  return Date.now();
}

/**
 * Calculates the difference in days between two date strings
 * 
 * This is a utility function that can be used for client-side validation
 * or display purposes (though server-side validation is authoritative).
 * 
 * @param date1 - First date string (YYYY-MM-DD)
 * @param date2 - Second date string (YYYY-MM-DD)  
 * @returns Number of days difference (positive if date1 is later)
 */
export function daysDifference(date1: string, date2: string): number {
  const d1 = parseUserDate(date1);
  const d2 = parseUserDate(date2);
  
  const diffTime = d1.getTime() - d2.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}